import "@/App.css";
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import VersionChecker from "@/components/VersionChecker";
import Layout from "@/components/Layout";
import AgentDetailLayout from "@/components/AgentDetailLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Directory from "@/pages/Directory";
import Profile from "@/pages/Profile";
import AlumniForm from "@/pages/AlumniForm";
import Classes from "@/pages/Classes";
import ClassDetail from "@/pages/ClassDetail";
import Import from "@/pages/Import";
import Backup from "@/pages/Backup";
import Communications from "@/pages/Communications";
import Fundraising from "@/pages/Fundraising";
import Reports from "@/pages/Reports";
import ClaimProfile from "@/pages/ClaimProfile";
import MyProfile from "@/pages/MyProfile";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import RsvpPage from "@/pages/RsvpPage";
import Experience from "@/pages/Experience";
import AlumniDirectory from "@/pages/AlumniDirectory";
import AlumniEvents from "@/pages/AlumniEvents";
import TowerMagazine from "@/pages/TowerMagazine";
import AlumniEventDetail from "@/pages/AlumniEventDetail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Unsubscribe from "@/pages/Unsubscribe";
import Privacy from "@/pages/Privacy";
import ClassAgent from "@/pages/ClassAgent";
import ClassAgents from "@/pages/ClassAgents";
import GroupsAdmin from "@/pages/GroupsAdmin";
import Give from "@/pages/Give";
import AccountSettings from "@/pages/AccountSettings";
import EngagementLeaderboard from "@/pages/EngagementLeaderboard";
import Users from "@/pages/Users";
import Branding from "@/pages/Branding";
import CareerNetwork from "@/pages/CareerNetwork";
import AlumniFaculty from "@/pages/AlumniFaculty";
import CareerAdmin from "@/pages/CareerAdmin";
import Faculty from "@/pages/Faculty";
import { BrandingProvider, BrandingWatermark } from "@/lib/branding";
import { GiveSuccess, GiveCancel } from "@/pages/GiveResult";
import Setup from "@/pages/Setup";
import LearnMore from "@/pages/LearnMore";
import { api } from "@/lib/api";

const isMobileViewport = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

function SetupGate({ children }) {
  const [status, setStatus] = useState(null);
  const location = useLocation();
  useEffect(() => {
    api.get("/setup/status").then((r) => setStatus(r.data)).catch(() => setStatus({ needs_setup: false }));
  }, []);
  if (status === null)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (status.needs_setup && location.pathname !== "/setup") return <Navigate to="/setup" replace />;
  if (!status.needs_setup && location.pathname === "/setup") return <Navigate to="/login" replace />;
  return children;
}

function Protected({ children, adminOnly, superadminOnly, fullAdminOnly }) {
  const { user, isAdmin, isSuperadmin, isFullAdmin } = useAuth();
  if (user === null)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "class_agent") return <Navigate to="/agent" replace />;
  if (user.role === "alumnus") return <Navigate to="/experience" replace />;
  if (superadminOnly && !isSuperadmin) return <Navigate to="/" replace />;
  if (fullAdminOnly && !isFullAdmin) return <Navigate to="/" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function ClassAgentRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "class_agent") return <Navigate to="/" replace />;
  // On mobile, class agents use the standard alumni experience — no class-agent portal/tools.
  if (isMobileViewport()) return <Navigate to="/experience" replace />;
  return children;
}

// Class-agent record view (no admin sidebar). Works for a real class agent AND an admin
// emulating a class agent (impersonated), so previewing never drops back into the admin shell.
function AgentViewRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const ok = user.role === "class_agent" || user.impersonated || user.role === "admin" || user.role === "superadmin";
  if (!ok) return <Navigate to="/" replace />;
  if (isMobileViewport() && user.role === "class_agent") return <Navigate to="/experience" replace />;
  return children;
}

function AlumnusRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <ThemeProvider>
        <BrandingProvider>
        <BrandingWatermark />
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <SetupGate>
          <Routes>
            <Route path="/setup" element={<Setup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/learn-more" element={<LearnMore />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/claim/:token" element={<ClaimProfile />} />
            <Route path="/rsvp/:token" element={<RsvpPage />} />
            <Route path="/give" element={<Give />} />
            <Route path="/give/success" element={<GiveSuccess />} />
            <Route path="/give/cancel" element={<GiveCancel />} />
            <Route path="/give/:campaignId" element={<Give />} />
            <Route path="/me" element={<AlumnusRoute><MyProfile /></AlumnusRoute>} />
            <Route path="/experience" element={<AlumnusRoute><Experience /></AlumnusRoute>} />
            <Route path="/experience/directory" element={<AlumnusRoute><AlumniDirectory /></AlumnusRoute>} />
            <Route path="/experience/careers" element={<AlumnusRoute><CareerNetwork /></AlumnusRoute>} />
            <Route path="/experience/faculty" element={<AlumnusRoute><AlumniFaculty /></AlumnusRoute>} />
            <Route path="/experience/events" element={<AlumnusRoute><AlumniEvents /></AlumnusRoute>} />
            <Route path="/experience/tower" element={<AlumnusRoute><TowerMagazine /></AlumnusRoute>} />
            <Route path="/experience/events/:id" element={<AlumnusRoute><AlumniEventDetail /></AlumnusRoute>} />
            <Route path="/agent" element={<ClassAgentRoute><ClassAgent /></ClassAgentRoute>} />
            <Route path="/agent/alumni/:id/edit" element={<AgentViewRoute><AlumniForm /></AgentViewRoute>} />
            <Route path="/agent/alumni/:id" element={<AgentViewRoute><AgentDetailLayout><Profile /></AgentDetailLayout></AgentViewRoute>} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/alumni" element={<Protected><Directory /></Protected>} />
            <Route path="/alumni/new" element={<Protected adminOnly><AlumniForm /></Protected>} />
            <Route path="/alumni/:id/edit" element={<Protected adminOnly><AlumniForm /></Protected>} />
            <Route path="/alumni/:id" element={<Protected><Profile /></Protected>} />
            <Route path="/engagement" element={<Protected><EngagementLeaderboard /></Protected>} />
            <Route path="/classes" element={<Protected><Classes /></Protected>} />
            <Route path="/classes/:year" element={<Protected><ClassDetail /></Protected>} />
            <Route path="/events" element={<Protected><Events /></Protected>} />
            <Route path="/events/:id" element={<Protected><EventDetail /></Protected>} />
            <Route path="/communications" element={<Protected><Communications /></Protected>} />
            <Route path="/class-agents" element={<Protected adminOnly><ClassAgents /></Protected>} />
            <Route path="/groups" element={<Protected adminOnly><GroupsAdmin /></Protected>} />
            <Route path="/fundraising" element={<Protected><Fundraising /></Protected>} />
            <Route path="/careers" element={<Protected><CareerAdmin /></Protected>} />
            <Route path="/faculty" element={<Protected><Faculty /></Protected>} />
            <Route path="/reports" element={<Protected fullAdminOnly><Reports /></Protected>} />
            <Route path="/import" element={<Protected adminOnly><Import /></Protected>} />
            <Route path="/backup" element={<Protected fullAdminOnly><Backup /></Protected>} />
            <Route path="/users" element={<Protected superadminOnly><Users /></Protected>} />
            <Route path="/branding" element={<Protected superadminOnly><Branding /></Protected>} />
            <Route path="/settings" element={<Protected><AccountSettings /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </SetupGate>
          <VersionChecker />
        </BrowserRouter>
        </BrandingProvider>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
